import { Card } from "@/components/ui";
import type { HomeFeedSummaryRow } from "@/types";
import { feedTypeLabel } from "@/utils/engagement";

function percent(rate: number | null) {
  if (rate === null) return "-";
  return `${(rate * 100).toFixed(1)}%`;
}

type Stage = { label: string; count: number; rate: number | null; note?: string };

function stagesFor(row: HomeFeedSummaryRow): Stage[] {
  return [
    { label: "생성", count: row.generated, rate: null },
    {
      label: "노출",
      count: row.impressed,
      rate: row.impression_rate,
      // Fired on feed load and lost if the app dies first — the weakest
      // number on this screen, so it says so rather than looking exact.
      note: "클라이언트 보고 · 유실 가능",
    },
    { label: "열람", count: row.opened, rate: row.open_rate },
    { label: "전환", count: row.converted, rate: row.conversion_rate },
  ];
}

/**
 * Generated → 노출 → 열람 → 전환, per card type.
 *
 * Bars are scaled against `generated` so the drop-off is visible at a glance;
 * the rate under each stage is against the **previous** stage, which is the
 * number you act on.
 */
export function HomeFeedFunnel({ rows }: { rows: HomeFeedSummaryRow[] }) {
  if (rows.length === 0) return null;

  return (
    <div className="grid gap-4 md:grid-cols-2">
      {rows.map((row) => {
        const stages = stagesFor(row);
        const max = Math.max(row.generated, 1);
        // Impressions are lossy, so a later stage can exceed an earlier one.
        // Showing it rather than clamping is the point — it is a real signal.
        const anomaly = stages.some((s) => s.rate !== null && s.rate > 1);
        return (
          <Card key={row.type} className="grid gap-3 p-4">
            <div className="flex items-baseline justify-between">
              <span className="font-semibold">{feedTypeLabel(row.type)}</span>
              <span className="text-sm text-muted-foreground">
                {row.generated}건 생성
              </span>
            </div>
            <div className="grid gap-2">
              {stages.map((stage) => (
                <div key={stage.label} className="grid gap-1">
                  <div className="flex items-baseline justify-between text-sm">
                    <span>{stage.label}</span>
                    <span className="text-muted-foreground">
                      {stage.count}
                      {stage.rate !== null && ` · ${percent(stage.rate)}`}
                    </span>
                  </div>
                  <div className="h-2 overflow-hidden rounded-full bg-surface-input">
                    <div
                      className="h-full rounded-full bg-primary"
                      style={{
                        width: `${Math.min((stage.count / max) * 100, 100)}%`,
                      }}
                    />
                  </div>
                  {stage.note && (
                    <span className="text-xs text-muted-foreground">{stage.note}</span>
                  )}
                </div>
              ))}
            </div>
            {anomaly && (
              <p className="text-xs text-muted-foreground">
                비율이 100%를 넘는 것은 오류가 아닙니다. 노출은 클라이언트 보고라 앱이
                종료되면 유실되고, 그 경우 전환이 자기 노출 기록보다 오래 남습니다.
              </p>
            )}
          </Card>
        );
      })}
    </div>
  );
}
